import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { uploadMassiveAttachment } from '@/services/massive-attachment-upload-service'

const DELAY_BETWEEN_UPLOADS_MS = 1000
const FINAL_STATUSES = ['uploaded', 'updated', 'replacement_created', 'skipped', 'failed']
const SUCCESS_STATUSES = ['uploaded', 'updated', 'replacement_created']

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds))

const errorMessageFor = (error) => {
  const data = error?.response?.data
  if (Array.isArray(data?.errors)) return data.errors.join(' ')
  return data?.error || data?.message || 'No se pudo cargar el archivo.'
}

export const useMassiveAttachmentUploadQueue = () => {
  const queueRef = useRef([])
  const runningRef = useRef(false)
  const [items, setItems] = useState([])
  const [isRunning, setIsRunning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [pauseReason, setPauseReason] = useState('')

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!runningRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warnBeforeLeaving)
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving)
  }, [])

  const replaceItems = useCallback((nextItems) => {
    queueRef.current = nextItems
    setItems(nextItems)
  }, [])

  const updateItem = useCallback((id, attributes) => {
    replaceItems(queueRef.current.map((item) => (
      item.id === id ? { ...item, ...attributes } : item
    )))
  }, [replaceItems])

  const processIds = useCallback(async (ids) => {
    if (runningRef.current) return queueRef.current

    runningRef.current = true
    setIsRunning(true)
    setIsPaused(false)
    setPauseReason('')
    let consecutiveServerErrors = 0

    for (let index = 0; index < ids.length; index += 1) {
      const id = ids[index]
      const item = queueRef.current.find((queuedItem) => queuedItem.id === id)
      if (!item) continue

      updateItem(id, { status: 'uploading', progress: 0, error: null })
      let shouldPause = false

      try {
        const response = await uploadMassiveAttachment({
          item,
          onProgress: (progress) => updateItem(id, {
            progress,
            status: progress >= 100 ? 'processing' : 'uploading'
          })
        })

        consecutiveServerErrors = 0
        updateItem(id, {
          status: response.status || 'uploaded',
          progress: 100,
          message: response.message,
          error: null,
          response
        })
      } catch (error) {
        const statusCode = error?.response?.status
        const isNetworkError = !error?.response
        const isServerError = statusCode >= 500
        consecutiveServerErrors = isServerError ? consecutiveServerErrors + 1 : 0

        updateItem(id, {
          status: 'failed',
          progress: 0,
          error: errorMessageFor(error)
        })

        if (statusCode === 401 || statusCode === 403) {
          shouldPause = true
          setPauseReason('La sesión o los permisos cambiaron. La carga fue detenida.')
          if (statusCode === 401) window.location.href = '/sign_in'
        } else if (isNetworkError) {
          shouldPause = true
          setPauseReason('Se perdió la conexión. La carga fue pausada.')
        } else if (consecutiveServerErrors >= 3) {
          shouldPause = true
          setPauseReason('El servidor respondió con varios errores consecutivos. La carga fue pausada.')
        }
      }

      if (shouldPause) {
        setIsPaused(true)
        break
      }

      if (index < ids.length - 1) await wait(DELAY_BETWEEN_UPLOADS_MS)
    }

    runningRef.current = false
    setIsRunning(false)
    return queueRef.current
  }, [updateItem])

  const start = useCallback(async (newItems) => {
    const pendingItems = newItems.map((item) => ({
      ...item,
      status: 'pending',
      progress: 0,
      error: null
    }))
    replaceItems(pendingItems)
    return processIds(pendingItems.map((item) => item.id))
  }, [processIds, replaceItems])

  const retryFailed = useCallback(async () => {
    const failedIds = queueRef.current
      .filter((item) => item.status === 'failed')
      .map((item) => item.id)

    failedIds.forEach((id) => updateItem(id, { status: 'pending', progress: 0, error: null }))
    return processIds(failedIds)
  }, [processIds, updateItem])

  const resumePending = useCallback(async () => {
    const pendingIds = queueRef.current
      .filter((item) => item.status === 'pending')
      .map((item) => item.id)

    return processIds(pendingIds)
  }, [processIds])

  const summary = useMemo(() => {
    const total = items.length
    const finalized = items.filter((item) => FINAL_STATUSES.includes(item.status)).length
    const successful = items.filter((item) => SUCCESS_STATUSES.includes(item.status)).length
    const skipped = items.filter((item) => item.status === 'skipped').length
    const failed = items.filter((item) => item.status === 'failed').length

    return {
      total,
      finalized,
      successful,
      skipped,
      failed,
      pending: total - finalized,
      percentage: total > 0 ? Math.round((finalized / total) * 100) : 0
    }
  }, [items])

  return {
    items,
    summary,
    isRunning,
    isPaused,
    pauseReason,
    start,
    retryFailed,
    resumePending
  }
}
